import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Play, Pause, Loader2, Type, PartyPopper, Wand2, Crown } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl, formatApiError } from "@/lib/api";
import { useNarration, wordWeights } from "@/hooks/useNarration";
import { Loader } from "@/components/ProtectedRoute";
import Paywall from "@/components/Paywall";
import { useAuth } from "@/context/AuthContext";

const SIZES = [["S", "text-xl sm:text-2xl"], ["M", "text-2xl sm:text-3xl"], ["L", "text-3xl sm:text-4xl"]];
const RATES = [0.75, 1, 1.25];

function HighlightedText({ text, progress, active, sizeClass }) {
  const words = useMemo(() => text.split(/\s+/), [text]);
  const marks = useMemo(() => wordWeights(words), [words]);
  const current = active ? marks.findIndex((m) => m >= progress) : -1;
  return (
    <p data-testid="reader-page-text" className={`font-story leading-relaxed ${sizeClass}`}>
      {words.map((w, i) => (
        <span key={i} className={i === current ? "word-active" : ""}>{w} </span>
      ))}
    </p>
  );
}

function Generating({ story }) {
  return (
    <div data-testid="reader-generating" className="mx-auto max-w-xl px-4 py-24 text-center">
      <Wand2 size={56} className="wobble mx-auto text-[color:var(--terracotta)]" />
      <h1 className="mt-6 font-display text-4xl font-bold">L'IA prépare ton histoire…</h1>
      <p className="mt-3 text-lg text-[color:var(--muted-ink)]">Elle écrit le texte puis peint chaque page. Cela prend environ une minute.</p>
      <p className="mt-6 font-story text-xl italic">{story.title}</p>
    </div>
  );
}

export default function Reader() {
  const { id } = useParams();
  const [story, setStory] = useState(null);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(1);
  const [finished, setFinished] = useState(false);
  const [locked, setLocked] = useState(false);
  const { user } = useAuth();
  const { play, stop, state, progress, rate, setRate, activeText } = useNarration();

  const load = useCallback(() => api.get(`/stories/${id}`).then((r) => setStory(r.data)).catch((e) => {
    if (e?.response?.status === 402) setLocked(true);
    else toast.error(formatApiError(e));
  }), [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (story?.status !== "generating") return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [story?.status, load]);

  if (locked) return <div className="px-4 py-16"><Paywall title="Cette histoire est réservée à Premium" /></div>;
  if (!story) return <Loader />;
  if (story.status === "generating") return <Generating story={story} />;
  if (story.status === "failed") return (
    <div data-testid="reader-failed" className="mx-auto max-w-xl px-4 py-24 text-center">
      <h1 className="font-display text-3xl font-bold">Oups, l'histoire n'a pas pu être créée.</h1>
      <Link to="/creer" className="btn-primary mt-6">Réessayer</Link>
    </div>
  );

  const p = story.pages[page];
  const last = page === story.pages.length - 1;
  const go = (d) => { stop(); setPage(page + d); };
  const finish = async () => {
    stop();
    try {
      await api.post("/progress", { type: "story_read", story_id: story.id });
      setFinished(true);
      toast.success("Bravo ! Histoire terminée !");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const isActive = activeText === p.text;

  return (
    <main data-testid="reader-page" className="mx-auto max-w-7xl px-4 pb-20 pt-6 sm:px-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link to="/bibliotheque" data-testid="reader-back-link" className="flex items-center gap-1 font-bold text-[color:var(--muted-ink)] hover:text-[color:var(--espresso)]"><ChevronLeft size={18} /> Bibliothèque</Link>
        <h1 className="font-display text-2xl font-bold sm:text-3xl">{story.title}</h1>
        <span data-testid="reader-page-counter" className="rounded-full bg-[color:var(--paper-2)] px-3 py-1 text-sm font-bold">Page {page + 1} sur {story.pages.length}</span>
      </div>

      <div key={page} className="rise card-paper mt-6 grid overflow-hidden md:grid-cols-2">
        <div className="bg-[color:var(--paper-2)]">
          {p.image_url ? <img src={mediaUrl(p.image_url)} alt="" data-testid="reader-page-image" className="h-full max-h-[70vh] w-full object-cover" />
            : <div className="grid h-full min-h-64 place-items-center text-[color:var(--muted-ink)]">Illustration indisponible</div>}
        </div>
        <div className="flex flex-col justify-between gap-8 p-6 sm:p-10">
          <HighlightedText text={p.text} progress={progress} active={isActive && state !== "loading"} sizeClass={SIZES[size][1]} />
          <div className="flex flex-wrap items-center gap-3">
            <button data-testid="reader-play-button" onClick={() => play(p.text)}
              className={`grid h-16 w-16 place-items-center rounded-full bg-[color:var(--terracotta)] text-white transition-transform hover:scale-105 ${isActive && state === "playing" ? "pulse-ring" : ""}`}>
              {isActive && state === "loading" ? <Loader2 className="animate-spin" /> : isActive && state === "playing" ? <Pause /> : <Play className="ml-1" />}
            </button>
            {!user.is_premium && (
              <Link to="/premium" data-testid="reader-narration-premium-link" className="flex items-center gap-1 rounded-full bg-[color:var(--sun)] px-3 py-1.5 text-sm font-extrabold"><Crown size={14} /> Voix Premium</Link>
            )}
            <div className="flex rounded-full bg-[color:var(--paper-2)] p-1">
              {RATES.map((r) => (
                <button key={r} data-testid={`reader-rate-${r}`} onClick={() => setRate(r)}
                  className={`rounded-full px-3 py-1.5 text-sm font-bold ${rate === r ? "bg-white shadow" : ""}`}>{r}x</button>
              ))}
            </div>
            <div className="flex items-center rounded-full bg-[color:var(--paper-2)] p-1">
              <Type size={16} className="mx-2 text-[color:var(--muted-ink)]" />
              {SIZES.map(([l], i) => (
                <button key={l} data-testid={`reader-size-${l}`} onClick={() => setSize(i)}
                  className={`rounded-full px-3 py-1.5 text-sm font-bold ${size === i ? "bg-white shadow" : ""}`}>{l}</button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between">
        <button data-testid="reader-prev-button" disabled={page === 0} onClick={() => go(-1)} className="btn-ghost disabled:opacity-40"><ChevronLeft /> Précédente</button>
        {!last ? (
          <button data-testid="reader-next-button" onClick={() => go(1)} className="btn-primary">Suivante <ChevronRight /></button>
        ) : !finished ? (
          <button data-testid="reader-finish-button" onClick={finish} className="btn-primary"><PartyPopper size={20} /> J'ai fini !</button>
        ) : (
          <Link to={`/atelier/${story.id}`} data-testid="reader-go-atelier-button" className="btn-primary">Aller à l'Atelier <ChevronRight /></Link>
        )}
      </div>
    </main>
  );
}
